import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';

interface SelectableChipOption {
  id: string;
  label: string;
}

interface IntensityOption {
  id: string;
  title: string;
  description: string;
}

interface UploadedFileItem {
  id: string;
  name: string;
  sizeLabel: string;
}

interface ModulePlan {
  id: string;
  title: string;
  timescale: string;
  summary: string;
  notes: string;
  moduleIndex?: number;
  designerNotes?: string;
  artifactRequirements?: {
    newsInject?: boolean;
    emailInject?: boolean;
    socialInject?: boolean;
  };
}

@Component({
  selector: 'app-step-six',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './step-six.component.html',
  styleUrl: './step-six.component.scss',
})
export class StepSixComponent {
  @Input() showLogisticsForm = true;
  @Input() generationError = '';
  @Input() isGenerating = false;

  // Logistics Form inputs
  @Input() durationOptions: SelectableChipOption[] = [];
  @Input() selectedDurationId: string | null = null;
  @Input() moduleCount = 0;
  @Output() moduleCountChange = new EventEmitter<number>();
  @Input() questionCount = 0;
  @Output() questionCountChange = new EventEmitter<number>();
  @Input() intensityOptions: IntensityOption[] = [];
  @Input() selectedIntensityId: string | null = null;
  @Input() includeNewsReports = false;
  @Input() includeSocialFeedSimulation = false;
  @Input() includeInternalEmails = false;
  @Input() socialPlatformOptions: SelectableChipOption[] = [];
  @Input() selectedSocialPlatformIds!: Set<string>;
  @Input() templateDragOver = false;
  @Input() templateFiles: UploadedFileItem[] = [];
  @Input() selectedDurationLabel = '';
  @Input() selectedIntensityTitle = '';

  // Module Plan Review inputs
  @Input() modulePlans: ModulePlan[] = [];

  selectDuration(durationId: string): void {
    this.selectedDurationId = durationId;
  }

  isDurationSelected(durationId: string): boolean {
    return this.selectedDurationId === durationId;
  }

  updateModuleCount(delta: number): void {
    this.moduleCount = Math.max(0, this.moduleCount + delta);
    this.moduleCountChange.emit(this.moduleCount);
  }

  updateQuestionCount(delta: number): void {
    this.questionCount = Math.max(0, this.questionCount + delta);
    this.questionCountChange.emit(this.questionCount);
  }

  formatCounterValue(value: number): string {
    return value.toString().padStart(2, '0');
  }

  selectIntensity(intensityId: string): void {
    this.selectedIntensityId = intensityId;
  }

  isIntensitySelected(intensityId: string): boolean {
    return this.selectedIntensityId === intensityId;
  }

  toggleSocialPlatform(platformId: string): void {
    if (this.selectedSocialPlatformIds.has(platformId)) {
      this.selectedSocialPlatformIds.delete(platformId);
      return;
    }

    this.selectedSocialPlatformIds.add(platformId);
  }

  isSocialPlatformSelected(platformId: string): boolean {
    return this.selectedSocialPlatformIds.has(platformId);
  }

  onTemplateSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    const newFile = {
      id: `template-${file.name}-${file.size}-${Date.now()}`,
      name: file.name,
      sizeLabel: this.formatFileSize(file.size),
    };

    this.templateFiles = [newFile];
    input.value = '';
  }

  onTemplateDragOver(event: DragEvent): void {
    event.preventDefault();
    this.templateDragOver = true;
  }

  onTemplateDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.templateDragOver = false;
  }

  onTemplateDrop(event: DragEvent): void {
    event.preventDefault();
    this.templateDragOver = false;

    const files = event.dataTransfer?.files;

    if (!files || files.length === 0) {
      return;
    }

    const file = files[0];
    const newFile = {
      id: `template-${file.name}-${file.size}-${Date.now()}`,
      name: file.name,
      sizeLabel: this.formatFileSize(file.size),
    };

    this.templateFiles = [newFile];
  }

  removeTemplateFile(fileId: string): void {
    this.templateFiles = this.templateFiles.filter(
      (file) => file.id !== fileId,
    );
  }

  updateModulePlanNote(moduleId: string, event: Event): void {
    const notes = (event.target as HTMLInputElement).value;
    const modulePlan = this.modulePlans.find((m) => m.id === moduleId);

    if (modulePlan) {
      modulePlan.notes = notes;
    }
  }

  private formatFileSize(sizeInBytes: number): string {
    if (!sizeInBytes) {
      return '0 KB';
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let value = sizeInBytes;
    let unitIndex = 0;

    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex += 1;
    }

    const roundedValue =
      value >= 10 || unitIndex === 0
        ? Math.round(value)
        : Number(value.toFixed(1));
    return `${roundedValue} ${units[unitIndex]}`;
  }
}
